import { useState, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Plus, ExternalLink, X, EyeOff, Check, CheckCircle2 } from 'lucide-react';
import { normalizeUrl, displayUrl } from './utils';

export const LinksCell = ({
  links = [], onChange, readOnly,
  hiddenUrls = [], showHidden = false, onHide, onUnhide, onChoose,
}) => {
  const [adding, setAdding] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const inputRef = useRef(null);

  // Normalize: support old single-string `link` field
  const linkList = Array.isArray(links) ? links.filter(Boolean) : (links ? [links] : []);
  const hiddenSet = new Set(hiddenUrls || []);

  // Decide which links to actually render: hide entries in hiddenSet
  // unless showHidden is on (room-level "Unhide all" toggle).
  const visibleLinks = linkList
    .map((url, idx) => ({ url, idx, hidden: hiddenSet.has(url) }))
    .filter((l) => showHidden || !l.hidden);

  // A link is the "chosen" one when there are 2+ links on the item and
  // every other link is hidden. We surface this with a filled green check
  // so the user can see at a glance which link they committed to.
  const visibleNotHiddenCount = linkList.filter((u) => !hiddenSet.has(u)).length;
  const isChosen = (url) => (
    linkList.length > 1
    && !hiddenSet.has(url)
    && visibleNotHiddenCount === 1
  );
  // Only show the choose-affordance once the item has 2+ links — with a
  // single link there's nothing to auto-hide.
  const canChoose = !!onChoose && linkList.length > 1;

  const addLink = () => {
    if (!newUrl.trim()) return;
    onChange([...linkList, newUrl.trim()]);
    setNewUrl('');
    setAdding(false);
  };

  const removeLink = (idx) => {
    onChange(linkList.filter((_, i) => i !== idx));
    setConfirmDelete(null);
  };

  if (readOnly) {
    return (
      <div className="space-y-0.5 px-1">
        {visibleLinks.length === 0 && <span className="text-xs text-[#5b6773]/40">-</span>}
        {visibleLinks.map(({ url, hidden }, i) => (
          <a
            key={`${url}-${i}`}
            href={normalizeUrl(url)}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs flex items-center gap-1 ${hidden ? 'text-[#9ca3af] line-through hover:no-underline' : 'text-[#2f6f8f] hover:underline'}`}
            data-testid={`link-anchor-${i}`}
          >
            {displayUrl(url)} <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
          </a>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-0.5 px-1 min-w-[110px]">
      {visibleLinks.map(({ url, idx, hidden }, i) => (
        <div key={`${url}-${idx}`} className="flex items-center gap-1 group/link">
          <a
            href={normalizeUrl(url)}
            target="_blank"
            rel="noopener noreferrer"
            className={`text-xs flex items-center gap-0.5 truncate flex-1 min-w-0 ${hidden ? 'text-[#9ca3af] line-through hover:no-underline italic' : 'text-[#2f6f8f] hover:underline'}`}
            data-testid={`link-anchor-${i}`}
            onClick={(e) => { if (hidden) { e.preventDefault(); } }}
          >
            {displayUrl(url)} <ExternalLink className="h-2.5 w-2.5 flex-shrink-0" />
          </a>
          {hidden ? (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {canChoose && (
                <button
                  type="button"
                  onClick={() => onChoose(url)}
                  className="p-0.5 rounded hover:bg-emerald-500/15 text-emerald-700"
                  title="Use this link — auto-hide the others"
                  data-testid={`link-choose-${idx}`}
                >
                  <Check className="h-3 w-3" />
                </button>
              )}
              <button
                type="button"
                onClick={() => onUnhide && onUnhide(url)}
                className="text-[9px] px-1.5 py-0.5 rounded bg-[#2d948c]/10 text-[#2d948c] hover:bg-[#2d948c]/20 transition-colors flex-shrink-0"
                title="Unhide this link permanently"
                data-testid={`link-unhide-${idx}`}
              >
                Unhide
              </button>
            </div>
          ) : confirmDelete === idx ? (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <button
                onClick={() => removeLink(idx)}
                className="text-[9px] px-1.5 py-0.5 rounded bg-destructive text-white hover:bg-destructive/90 transition-colors"
                data-testid={`link-confirm-delete-${idx}`}
              >
                Delete
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="text-[9px] px-1 py-0.5 rounded bg-gray-200 hover:bg-gray-300 text-gray-600 transition-colors"
                data-testid={`link-cancel-delete-${idx}`}
              >
                No
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              {canChoose && (
                isChosen(url) ? (
                  <span
                    className="p-0.5 rounded text-emerald-600"
                    title="Chosen link — others are hidden"
                    data-testid={`link-chosen-${idx}`}
                  >
                    <CheckCircle2 className="h-3 w-3 fill-emerald-100" />
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onChoose(url)}
                    className="opacity-0 group-hover/link:opacity-100 transition-opacity p-0.5 rounded hover:bg-emerald-500/15 text-emerald-700"
                    title="Use this link — auto-hide the others"
                    data-testid={`link-choose-${idx}`}
                  >
                    <Check className="h-3 w-3" />
                  </button>
                )
              )}
              {onHide && (
                <button
                  type="button"
                  onClick={() => onHide(url)}
                  className="opacity-0 group-hover/link:opacity-100 transition-opacity p-0.5 rounded hover:bg-[#5b6773]/10 text-[#5b6773]"
                  title="Hide this link"
                  data-testid={`link-hide-${idx}`}
                >
                  <EyeOff className="h-3 w-3" />
                </button>
              )}
              <button
                onClick={() => setConfirmDelete(idx)}
                className="opacity-0 group-hover/link:opacity-100 transition-opacity p-0.5 rounded hover:bg-destructive/10 text-destructive"
                data-testid={`link-delete-${idx}`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      ))}

      {adding ? (
        <div className="flex items-center gap-1">
          <Input
            ref={inputRef}
            value={newUrl}
            onChange={e => setNewUrl(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addLink(); if (e.key === 'Escape') { setAdding(false); setNewUrl(''); } }}
            onBlur={() => { if (newUrl.trim()) addLink(); else setAdding(false); }}
            placeholder="Paste URL..."
            className="h-6 text-[10px] px-1.5 flex-1"
            autoFocus
            data-testid="add-link-input"
          />
        </div>
      ) : (
        <button
          onClick={() => { setAdding(true); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="text-[10px] text-[#2d948c] hover:text-[#2d948c]/80 flex items-center gap-0.5 transition-colors"
          data-testid="add-link-btn"
        >
          <Plus className="h-2.5 w-2.5" /> Add link
        </button>
      )}
    </div>
  );
};
